import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

const COLOR_BY_STATUS = {
  overdue: '#DC2626',
  completed: '#10B981',
  pending_approval: '#F59E0B',
  cancelled: '#9CA3AF',
};

const COLOR_BY_TYPE = {
  meeting: '#3B82F6',
  site_visit: '#8B5CF6',
  follow_up: '#F97316',
  deadline: '#EF4444',
  callback: '#06B6D4',
  pipeline_review: '#6366F1',
  task: '#64748B',
  reminder: '#A855F7',
  agent_action: '#14B8A6',
  post_meeting_review: '#F59E0B',
  pre_meeting_briefing: '#0EA5E9',
};

// Onda 3.5 — cor fixa por colaborador para distinção visual no calendário
export const COLABORADOR_COLORS = {
  eduardo:  { bg: '#3B82F6', text: '#FFFFFF', initial: 'ET', name: 'Eduardo',  bgLight: '#DBEAFE' },
  caroline: { bg: '#EC4899', text: '#FFFFFF', initial: 'CV', name: 'Caroline', bgLight: '#FCE7F3' },
  fabio:    { bg: '#10B981', text: '#FFFFFF', initial: 'FR', name: 'Fábio',    bgLight: '#D1FAE5' },
};

const FALLBACK_COLOR = { bg: '#94A3B8', text: '#FFFFFF', initial: '?', name: '—', bgLight: '#F1F5F9' };

// Mapa email->membro para identificar quem da equipe esta entre os attendees
const TEAM_EMAILS = {
  'eduardo@astoncapital.com.br':  { initial: 'ET', bg: '#3B82F6', text: '#FFFFFF', name: 'Eduardo'  },
  'caroline@astoncapital.com.br': { initial: 'CV', bg: '#EC4899', text: '#FFFFFF', name: 'Caroline' },
  'fabio@spareas.com.br':         { initial: 'FR', bg: '#10B981', text: '#FFFFFF', name: 'Fábio'    },
};

function deriveTeamAttendees(attendees, ownerColaboradorId) {
  if (Array.isArray(attendees) && attendees.length > 0) {
    const seen = new Set();
    const matched = [];
    for (const att of attendees) {
      const email = (att?.email || '').trim().toLowerCase();
      if (!email) continue;
      const member = TEAM_EMAILS[email];
      if (member && !seen.has(email)) {
        seen.add(email);
        matched.push(member);
      }
    }
    if (matched.length > 0) return matched;
  }
  // Fallback: owner_colaborador_id
  const fallback = COLABORADOR_COLORS[ownerColaboradorId];
  return fallback ? [fallback] : [];
}

function getEventColor(eventType, status) {
  if (COLOR_BY_STATUS[status]) return COLOR_BY_STATUS[status];
  return COLOR_BY_TYPE[eventType] || '#64748B';
}

export const useCalendarFeed = (filters = {}) => {
  const { colaboradorId, businessUnitId, eventType } = filters;
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('v_calendar_feed')
        .select('*')
        .order('start_at', { ascending: true });

      if (colaboradorId && colaboradorId !== 'all') {
        query = query.or(
          `owner_colaborador_id.eq.${colaboradorId},assigned_to_colaborador_id.eq.${colaboradorId}`
        );
      }
      if (businessUnitId) {
        query = query.eq('business_unit_id', businessUnitId);
      }
      if (eventType) {
        query = query.eq('event_type', eventType);
      }

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setEvents(data || []);
    } catch (err) {
      console.error('useCalendarFeed error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [colaboradorId, businessUnitId, eventType]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const calendarEvents = useMemo(() => {
    return events.map((evt) => {
      const typeColor = getEventColor(evt.event_type, evt.status);
      const isExternal = evt.source_table === 'external_calendar_events';
      const colabColor = COLABORADOR_COLORS[evt.owner_colaborador_id] || null;
      const colabColorOrFallback = colabColor || FALLBACK_COLOR;

      // Externos: fundo translúcido (bgLight) + borda sólida na cor do colaborador
      // Internos (REIP): fundo sólido na cor do tipo + borda na cor do colaborador
      const backgroundColor = isExternal ? colabColorOrFallback.bgLight : typeColor;
      const textColor = isExternal ? colabColorOrFallback.bg : undefined;

      // Onda 5 — evento REIP "scheduled" que já passou precisa de registro
      const startMs = evt.start_at ? Date.parse(evt.start_at) : NaN;
      const endMs = evt.end_at ? Date.parse(evt.end_at) : startMs;
      const needsReview = !isExternal
        && evt.source_table === 'scheduled_events'
        && evt.status === 'scheduled'
        && Number.isFinite(endMs)
        && endMs < Date.now();

      const borderColor = needsReview ? '#EF4444' : colabColorOrFallback.bg;

      const classNames = [];
      if (isExternal) classNames.push('external-event');
      if (needsReview) classNames.push('needs-review');

      return {
        id: `${evt.source_table}:${evt.source_id}`,
        title: evt.title,
        start: evt.start_at,
        end: evt.end_at,
        allDay: evt.all_day,
        backgroundColor,
        borderColor,
        textColor,
        editable: evt.is_editable && !isExternal,
        durationEditable: evt.is_editable && !isExternal,
        classNames,
        extendedProps: {
          isExternal,
          sourceId: evt.source_id,
          sourceTable: evt.source_table,
          isEditable: evt.is_editable,
          description: evt.description,
          eventType: evt.event_type,
          status: evt.status,
          priority: evt.priority,
          visibility: evt.visibility,
          ownerColaboradorId: evt.owner_colaborador_id,
          colaboradorId: evt.owner_colaborador_id, // alias para componentes (hover/modal)
          assignedToColaboradorId: evt.assigned_to_colaborador_id,
          businessUnitId: evt.business_unit_id,
          createdByType: evt.created_by_type,
          createdByAgent: evt.created_by_agent,
          requiresApproval: evt.requires_approval,
          meetUrl: evt.meet_url,
          // Onda 3.5 — campos para modal/tooltip
          colaboradorColor: colabColor,
          location: evt.location,
          attendees: evt.attendees,
          organizer: evt.organizer,
          teamAttendees: deriveTeamAttendees(evt.attendees, evt.owner_colaborador_id),
          needsReview,
        },
      };
    });
  }, [events]);

  return { events: calendarEvents, rawEvents: events, loading, error, refetch: fetchEvents };
};

export default useCalendarFeed;
